import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Download, History } from "lucide-react";
import api from "../lib/api";

const ACTIONS = [
  { v: "", l: "All actions" },
  { v: "edit,edit_request", l: "Edits" },
  { v: "delete,delete_request,cancel,cancel_request", l: "Deletes / cancels" },
  { v: "lock,unlock", l: "Lock / unlock" },
  { v: "approve", l: "Approvals" },
];
const ACTION_LABEL = {
  edit: "Edit", edit_request: "Edit request", delete: "Delete", delete_request: "Delete request",
  cancel: "Cancel", cancel_request: "Cancel request", lock: "Lock", unlock: "Unlock", approve: "Approved",
};
const ctl = "h-10 px-2 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "");

const exportCsv = (rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [["Date & time", "User", "Action", "Details", "Entry date"].join(","),
    ...rows.map((r) => [fmt(r.at), r.user_name, ACTION_LABEL[r.action] || r.action, r.details, r.entry_date].map(esc).join(","))];
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" }));
  a.download = "user_activity.csv"; a.click(); URL.revokeObjectURL(a.href);
};

const Filters = ({ f, setF, users, onExport }) => (
  <div className="flex flex-wrap items-end gap-3 mb-4">
    <input data-testid="activity-start" type="date" className={`${ctl} num`} value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} />
    <input data-testid="activity-end" type="date" className={`${ctl} num`} value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} />
    <select data-testid="activity-user" className={ctl} value={f.user_id} onChange={(e) => setF({ ...f, user_id: e.target.value })}>
      <option value="">All users</option>
      {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
    </select>
    <select data-testid="activity-action" className={ctl} value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })}>
      {ACTIONS.map((a) => <option key={a.v} value={a.v}>{a.l}</option>)}
    </select>
    <button data-testid="activity-export" onClick={onExport} className="h-10 px-3 rounded-md border border-input bg-card text-sm flex items-center gap-1.5 hover:bg-muted transition-colors"><Download size={14} /> Download CSV</button>
  </div>
);

export const UserActivityReport = ({ users }) => {
  const [f, setF] = useState({ start: "", end: "", user_id: "", action: "" });
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    try { setRows((await api.get("/reports/user-activity", { params })).data); } catch (e) { console.error(e); }
  }, [f]);
  useEffect(() => { load(); }, [load]);
  const shown = useMemo(() => rows.slice(0, 500), [rows]);

  return (
    <section data-testid="user-activity-report" className="mt-10">
      <h2 className="font-display text-base md:text-lg font-semibold mb-1 flex items-center gap-2"><History size={17} /> User activity</h2>
      <p className="text-xs text-muted-foreground mb-4">Every edit, delete, approval and cash book lock / unlock, with who did it and when.</p>
      <Filters f={f} setF={setF} users={users} onExport={() => exportCsv(rows)} />
      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <div className="min-w-[760px]">
          <div className="grid grid-cols-[170px_150px_120px_minmax(0,1fr)_100px] gap-4 px-5 py-3 bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <span>Date & time</span><span>User</span><span>Action</span><span>Details</span><span>Entry date</span>
          </div>
          {shown.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">No activity in this period.</div> : (
            <div className="divide-y divide-border">
              {shown.map((r) => (
                <div key={r.id} data-testid={`activity-row-${r.id}`} className="grid grid-cols-[170px_150px_120px_minmax(0,1fr)_100px] gap-4 px-5 py-3 text-sm items-start">
                  <span className="num text-xs">{fmt(r.at)}</span>
                  <span className="font-medium break-words">{r.user_name}</span>
                  <span className="text-xs font-semibold uppercase text-primary">{ACTION_LABEL[r.action] || r.action}</span>
                  <span className="break-words">{r.details}</span>
                  <span className="num text-xs text-muted-foreground">{r.entry_date}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
